/**
 * SystemMap
 *
 * A component for visualizing complex systems, dependencies, and relationships
 *
 * Primary responsibilities:
 * - Display interconnected nodes and relationships
 * - Visualize dependencies between system components
 * - Support different node types for different system elements
 * - Allow exploring complex relationships through interaction
 */

import React, { useState, useRef, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { IconNetwork, IconZoomIn, IconZoomOut, IconArrowsMaximize } from '@tabler/icons-react'

// Node represents a component in the system
export interface SystemNode {
  id: string
  label: string
  type: 'service' | 'database' | 'api' | 'user' | 'process' | 'component' | string
  description?: string
  group?: string
  icon?: string
  metadata?: Record<string, any>
  size?: 'small' | 'medium' | 'large'
  status?: 'active' | 'warning' | 'error' | 'inactive'
  position?: { x: number; y: number }
}

// Connection represents a relationship between nodes
export interface SystemConnection {
  source: string // ID of source node
  target: string // ID of target node
  label?: string
  type?: 'dependency' | 'dataflow' | 'request' | 'association' | string
  direction?: 'unidirectional' | 'bidirectional'
  strength?: 'weak' | 'medium' | 'strong'
  metadata?: Record<string, any>
  status?: 'active' | 'warning' | 'error' | 'inactive'
}

// Group of nodes
export interface SystemGroup {
  id: string
  name: string
  description?: string
  color?: string
}

// Main system map data structure
export interface SystemMapData {
  title: string
  description?: string
  nodes: SystemNode[]
  connections: SystemConnection[]
  groups?: SystemGroup[]
  viewBox?: string
  layout?: 'force' | 'hierarchical' | 'circular' | 'grid'
}

interface SystemMapProps {
  data: SystemMapData
  className?: string
}

export const SystemMap: React.FC<SystemMapProps> = ({ data, className }) => {
  const [selectedNode, setSelectedNode] = useState<string | null>(null)
  const [zoom, setZoom] = useState(1)
  const [viewBoxDimensions, setViewBoxDimensions] = useState({ width: 1000, height: 600 })
  const mapRef = useRef<HTMLDivElement>(null)

  // Get node position either from defined position or calculate based on index
  const getNodePosition = (node: SystemNode, index: number) => {
    if (node.position) return node.position

    // Simple positioning algorithm based on layout
    switch (data.layout) {
      case 'circular':
        const angle = (index / data.nodes.length) * 2 * Math.PI
        const radius = Math.min(viewBoxDimensions.width, viewBoxDimensions.height) * 0.4
        return {
          x: viewBoxDimensions.width / 2 + Math.cos(angle) * radius,
          y: viewBoxDimensions.height / 2 + Math.sin(angle) * radius
        }
      case 'grid':
        const cols = Math.ceil(Math.sqrt(data.nodes.length))
        const col = index % cols
        const row = Math.floor(index / cols)
        const cellWidth = viewBoxDimensions.width / (cols + 1)
        const cellHeight = viewBoxDimensions.height / (Math.ceil(data.nodes.length / cols) + 1)
        return {
          x: cellWidth * (col + 1),
          y: cellHeight * (row + 1)
        }
      default: // Force or hierarchical
        // Simple spread across the view
        return {
          x: 100 + ((index % 5) * (viewBoxDimensions.width - 200)) / 5,
          y:
            100 +
            (Math.floor(index / 5) * (viewBoxDimensions.height - 200)) /
              Math.ceil(data.nodes.length / 5)
        }
    }
  }

  // Get nodes that are connected to the selected node
  const getConnectedNodes = (nodeId: string) => {
    return data.connections
      .filter((conn) => conn.source === nodeId || conn.target === nodeId)
      .map((conn) => (conn.source === nodeId ? conn.target : conn.source))
  }

  // Determine if a node should be highlighted based on selection
  const isHighlighted = (nodeId: string) => {
    if (!selectedNode) return false
    return selectedNode === nodeId || getConnectedNodes(selectedNode).includes(nodeId)
  }

  // Get node color based on type or group
  const getNodeColor = (node: SystemNode) => {
    // Check if node belongs to a group
    if (node.group && data.groups) {
      const group = data.groups.find((g) => g.id === node.group)
      if (group?.color) return group.color
    }

    // Colors based on node type
    switch (node.type) {
      case 'service':
        return '#4f46e5' // Indigo
      case 'database':
        return '#16a34a' // Green
      case 'api':
        return '#ea580c' // Orange
      case 'user':
        return '#0ea5e9' // Sky blue
      case 'process':
        return '#d946ef' // Fuchsia
      case 'component':
        return '#6b7280' // Gray
      default:
        return '#6b7280' // Default gray
    }
  }

  // Get node size based on size property or default to medium
  const getNodeSize = (node: SystemNode) => {
    switch (node.size) {
      case 'small':
        return 12
      case 'large':
        return 24
      case 'medium':
      default:
        return 18
    }
  }

  // Get connection color based on type and status
  const getConnectionColor = (connection: SystemConnection) => {
    if (connection.status === 'error') return '#ef4444' // Red
    if (connection.status === 'warning') return '#f59e0b' // Amber
    if (connection.status === 'inactive') return '#9ca3af' // Gray

    switch (connection.type) {
      case 'dependency':
        return '#3b82f6' // Blue
      case 'dataflow':
        return '#10b981' // Emerald
      case 'request':
        return '#8b5cf6' // Violet
      default:
        return '#6b7280' // Gray
    }
  }

  // Get connection style (dashed, solid, etc.) based on strength
  const getConnectionStyle = (connection: SystemConnection) => {
    switch (connection.strength) {
      case 'weak':
        return '2,2'
      case 'medium':
        return ''
      case 'strong':
        return ''
      default:
        return ''
    }
  }

  // Get connection width based on strength
  const getConnectionWidth = (connection: SystemConnection) => {
    switch (connection.strength) {
      case 'weak':
        return 1
      case 'medium':
        return 1.5
      case 'strong':
        return 2
      default:
        return 1.5
    }
  }

  // Handle zoom in/out
  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.2, 2))
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.2, 0.5))
  const handleResetZoom = () => setZoom(1)

  // Handle node click
  const handleNodeClick = (nodeId: string) => {
    setSelectedNode((prev) => (prev === nodeId ? null : nodeId))
  }

  return (
    <div
      className={cn(
        'border-border bg-card my-4 flex flex-col overflow-hidden rounded-lg border shadow-sm transition-all',
        className
      )}
    >
      <div className="flex items-center justify-between border-b p-3">
        <div className="flex items-center gap-2">
          <IconNetwork size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-medium">{data.title}</h3>
        </div>

        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            onClick={handleZoomIn}
            title="Zoom In"
          >
            <IconZoomIn size={14} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            onClick={handleZoomOut}
            title="Zoom Out"
          >
            <IconZoomOut size={14} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            onClick={handleResetZoom}
            title="Reset"
          >
            <IconArrowsMaximize size={14} />
          </Button>
        </div>
      </div>

      {data.description && (
        <div className="text-muted-foreground border-b px-4 py-2 text-xs">{data.description}</div>
      )}

      <div className="overflow-hidden p-4" ref={mapRef} style={{ height: 400 }}>
        <div
          className="relative size-full"
          style={{
            transform: `scale(${zoom})`,
            transformOrigin: 'center',
            transition: 'transform 0.2s'
          }}
        >
          <svg
            width="100%"
            height="100%"
            viewBox={data.viewBox || `0 0 ${viewBoxDimensions.width} ${viewBoxDimensions.height}`}
            className="bg-muted/20 rounded-md"
          >
            {/* Render groups first as backgrounds */}
            {data.groups && (
              <>
                {data.groups.map((group) => {
                  // Find all nodes in this group
                  const groupNodes = data.nodes.filter((node) => node.group === group.id)
                  if (groupNodes.length === 0) return null

                  // Calculate bounding box
                  const positions = groupNodes.map((node, idx) =>
                    getNodePosition(
                      node,
                      data.nodes.findIndex((n) => n.id === node.id)
                    )
                  )
                  const minX = Math.min(...positions.map((p) => p.x)) - 40
                  const minY = Math.min(...positions.map((p) => p.y)) - 40
                  const maxX = Math.max(...positions.map((p) => p.x)) + 40
                  const maxY = Math.max(...positions.map((p) => p.y)) + 40

                  return (
                    <rect
                      key={`group-${group.id}`}
                      x={minX}
                      y={minY}
                      width={maxX - minX}
                      height={maxY - minY}
                      rx={8}
                      fill={group.color || '#f3f4f6'}
                      fillOpacity={0.2}
                      stroke={group.color || '#d1d5db'}
                      strokeWidth={1}
                    />
                  )
                })}
              </>
            )}

            {/* Render connections */}
            {data.connections.map((connection, idx) => {
              const sourceNode = data.nodes.find((node) => node.id === connection.source)
              const targetNode = data.nodes.find((node) => node.id === connection.target)

              if (!sourceNode || !targetNode) return null

              const sourceIndex = data.nodes.findIndex((node) => node.id === sourceNode.id)
              const targetIndex = data.nodes.findIndex((node) => node.id === targetNode.id)

              const sourcePos = getNodePosition(sourceNode, sourceIndex)
              const targetPos = getNodePosition(targetNode, targetIndex)

              // Determine if this connection should be highlighted
              const isConnHighlighted =
                selectedNode && (selectedNode === sourceNode.id || selectedNode === targetNode.id)

              // Calculate path
              const dx = targetPos.x - sourcePos.x
              const dy = targetPos.y - sourcePos.y
              const dr =
                Math.sqrt(dx * dx + dy * dy) * (connection.direction === 'bidirectional' ? 1.2 : 1)

              // For bidirectional, create a curved path
              const path =
                connection.direction === 'bidirectional'
                  ? `M${sourcePos.x},${sourcePos.y} A${dr},${dr} 0 0,1 ${targetPos.x},${targetPos.y}`
                  : `M${sourcePos.x},${sourcePos.y} L${targetPos.x},${targetPos.y}`

              return (
                <g key={`connection-${idx}`}>
                  <path
                    d={path}
                    fill="none"
                    stroke={getConnectionColor(connection)}
                    strokeWidth={getConnectionWidth(connection)}
                    strokeDasharray={getConnectionStyle(connection)}
                    opacity={isConnHighlighted || !selectedNode ? 1 : 0.2}
                    markerEnd={
                      connection.direction !== 'bidirectional'
                        ? `url(#arrow-${getConnectionColor(connection).replace('#', '')})`
                        : ''
                    }
                    className="transition-opacity duration-300"
                  />
                  {connection.label && (
                    <text
                      x={(sourcePos.x + targetPos.x) / 2}
                      y={(sourcePos.y + targetPos.y) / 2 - 5}
                      textAnchor="middle"
                      fontSize="10"
                      fill="currentColor"
                      className="text-muted-foreground"
                      opacity={isConnHighlighted || !selectedNode ? 1 : 0.2}
                    >
                      {connection.label}
                    </text>
                  )}
                </g>
              )
            })}

            {/* Define arrow markers for each color */}
            <defs>
              {[
                '#4f46e5',
                '#16a34a',
                '#ea580c',
                '#0ea5e9',
                '#d946ef',
                '#6b7280',
                '#ef4444',
                '#f59e0b',
                '#3b82f6',
                '#10b981',
                '#8b5cf6'
              ].map((color) => (
                <marker
                  key={`arrow-${color.replace('#', '')}`}
                  id={`arrow-${color.replace('#', '')}`}
                  viewBox="0 0 10 10"
                  refX="8"
                  refY="5"
                  markerWidth="6"
                  markerHeight="6"
                  orient="auto-start-reverse"
                >
                  <path d="M 0 0 L 10 5 L 0 10 z" fill={color} />
                </marker>
              ))}
            </defs>

            {/* Render nodes */}
            {data.nodes.map((node, idx) => {
              const position = getNodePosition(node, idx)
              const nodeColor = getNodeColor(node)
              const nodeSize = getNodeSize(node)
              const highlighted = isHighlighted(node.id) || !selectedNode

              return (
                <g
                  key={`node-${node.id}`}
                  transform={`translate(${position.x}, ${position.y})`}
                  onClick={() => handleNodeClick(node.id)}
                  className="cursor-pointer"
                >
                  <circle
                    r={nodeSize}
                    fill={nodeColor}
                    stroke={selectedNode === node.id ? '#000' : nodeColor}
                    strokeWidth={selectedNode === node.id ? 2 : 1}
                    opacity={highlighted ? 1 : 0.2}
                    className="transition-opacity duration-300"
                  />
                  {node.status === 'error' && (
                    <circle
                      r={nodeSize * 1.3}
                      fill="none"
                      stroke="#ef4444"
                      strokeWidth={2}
                      opacity={0.6}
                    />
                  )}
                  {node.status === 'warning' && (
                    <circle
                      r={nodeSize * 1.3}
                      fill="none"
                      stroke="#f59e0b"
                      strokeWidth={2}
                      opacity={0.6}
                    />
                  )}
                  <text
                    textAnchor="middle"
                    fontSize="10"
                    dy="4"
                    fill="#fff"
                    opacity={highlighted ? 1 : 0.2}
                    className="pointer-events-none select-none"
                  >
                    {node.label.slice(0, 1).toUpperCase()}
                  </text>
                  <text
                    textAnchor="middle"
                    fontSize="10"
                    y={nodeSize + 12}
                    fill="currentColor"
                    className="text-foreground pointer-events-none select-none"
                    opacity={highlighted ? 1 : 0.2}
                  >
                    {node.label}
                  </text>
                </g>
              )
            })}
          </svg>
        </div>
      </div>

      {/* Show information panel for selected node */}
      {selectedNode && (
        <div className="border-t px-4 py-3">
          {(() => {
            const node = data.nodes.find((n) => n.id === selectedNode)
            if (!node) return null

            const connectedTo = data.connections
              .filter((conn) => conn.source === selectedNode)
              .map((conn) => ({
                nodeId: conn.target,
                type: conn.type || 'connection',
                label: conn.label
              }))

            const connectedFrom = data.connections
              .filter((conn) => conn.target === selectedNode)
              .map((conn) => ({
                nodeId: conn.source,
                type: conn.type || 'connection',
                label: conn.label
              }))

            return (
              <div className="text-xs">
                <div className="mb-2 flex items-center gap-2">
                  <div
                    className="size-3 rounded-full"
                    style={{ backgroundColor: getNodeColor(node) }}
                  ></div>
                  <h4 className="font-medium">{node.label}</h4>
                  <span className="text-muted-foreground">({node.type})</span>
                </div>

                {node.description && (
                  <p className="text-muted-foreground mb-2">{node.description}</p>
                )}

                {(connectedTo.length > 0 || connectedFrom.length > 0) && (
                  <div className="mt-2">
                    {connectedTo.length > 0 && (
                      <div className="mb-1">
                        <span className="font-medium">Connects to: </span>
                        {connectedTo.map((conn, i) => {
                          const targetNode = data.nodes.find((n) => n.id === conn.nodeId)
                          return (
                            <span key={`to-${i}`} className="text-muted-foreground">
                              {i > 0 && ', '}
                              <span
                                className="cursor-pointer hover:underline"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleNodeClick(conn.nodeId)
                                }}
                              >
                                {targetNode?.label || conn.nodeId}
                              </span>
                              {conn.label && <span> ({conn.label})</span>}
                            </span>
                          )
                        })}
                      </div>
                    )}

                    {connectedFrom.length > 0 && (
                      <div>
                        <span className="font-medium">Connected from: </span>
                        {connectedFrom.map((conn, i) => {
                          const sourceNode = data.nodes.find((n) => n.id === conn.nodeId)
                          return (
                            <span key={`from-${i}`} className="text-muted-foreground">
                              {i > 0 && ', '}
                              <span
                                className="cursor-pointer hover:underline"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleNodeClick(conn.nodeId)
                                }}
                              >
                                {sourceNode?.label || conn.nodeId}
                              </span>
                              {conn.label && <span> ({conn.label})</span>}
                            </span>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })()}
        </div>
      )}
    </div>
  )
}
